/**
 * One notice as an admin sees it: everything the employee view hides.
 *
 * The row states provenance before it offers actions. An `IVANTI` notice is
 * badged as locked and its edit button is labelled "placement only", because
 * the rule that its content is read-only is a contract fact an admin needs
 * *before* clicking, not a 409 they meet afterwards. Delete is absent on those
 * rows entirely rather than present-and-failing — Ivanti withdraws its own
 * alerts, and offering a button that cannot work is worse than offering none.
 *
 * Who and when is shown for both creation and the last edit, on every row. On a
 * board that any admin can change and that every employee reads, "who put this
 * on the home page, and when" has to be answerable from the list itself.
 */

import { Clock, Icon, Lock, Pencil, Trash2 } from "@norican/icons";
import { AlertDialog, Badge, Button, Card, Typography } from "@norican/react";
import { useState } from "react";

import { Markdown, RelativeTime } from "@/components/common";
import { useT } from "@/i18n";
import type { Message } from "@/lib/api";

import { useDeleteMessage, useUpdateMessage } from "./api";
import {
  isReadOnlySource,
  MESSAGE_KIND_TONE,
  useMessageKindLabel,
  useMessagePlacementLabel,
} from "./message-kinds";

export interface AdminMessageRowProps {
  message: Message;
  onEdit: (message: Message) => void;
  onShowHistory: (messageId: string) => void;
}

export function AdminMessageRow({ message, onEdit, onShowHistory }: AdminMessageRowProps) {
  const { t } = useT("messages");
  const kindLabel = useMessageKindLabel();
  const placementLabel = useMessagePlacementLabel();

  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const update = useUpdateMessage();
  const remove = useDeleteMessage();

  // Ivanti's words and the app's are both read-only here; only the
  // placement and rank are an admin's. See `isReadOnlySource`.
  const isReadOnly = isReadOnlySource(message.source);
  const isPublished = message.published_at != null;
  const tone = MESSAGE_KIND_TONE[message.kind];
  const body = message.body_markdown ?? "";

  return (
    <Card variant="outline" padding="md" className="flex w-full flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={tone} variant="subtle" size="sm">
          {kindLabel(message.kind)}
        </Badge>
        <Badge tone={isPublished ? "success" : "neutral"} variant="outline" size="sm">
          {isPublished ? t("admin.published") : t("admin.draft")}
        </Badge>
        <Badge tone="neutral" variant="outline" size="sm">
          {placementLabel(message.placement)}
        </Badge>
        <Badge tone="neutral" variant="outline" size="sm" className="font-mono">
          {t("admin.rankBadge", { rank: message.rank ?? 0 })}
        </Badge>
        {isReadOnly ? (
          <Badge tone="info" variant="subtle" size="sm" className="gap-1">
            <Icon icon={Lock} size="xs" />
            {message.source === "IVANTI" ? t("source.IVANTI") : t("source.BUILTIN")}
          </Badge>
        ) : (
          <Badge tone="neutral" variant="subtle" size="sm">
            {t("source.LOCAL")}
          </Badge>
        )}
        {/* Only an explicit `false` means Ivanti closed the alert; the field
            is absent on rows that were never external. */}
        {message.external_active === false ? (
          <Badge tone="danger" variant="subtle" size="sm">
            {t("admin.withdrawn")}
          </Badge>
        ) : null}
      </div>

      <Typography as="h3" variant="heading-5">
        {message.title}
      </Typography>

      {body.trim() === "" ? null : (
        <div className="border-border-subtle max-h-48 overflow-y-auto rounded-md border p-3">
          <Markdown>{body}</Markdown>
        </div>
      )}

      <div className="text-fg-muted flex flex-wrap gap-x-6 gap-y-1 text-xs">
        <span>
          {t("admin.createdBy", { actor: message.created_by })}{" "}
          <RelativeTime value={message.created_at} mode="absolute" />
        </span>
        <span>
          {t("admin.updatedBy", { actor: message.updated_by })}{" "}
          <RelativeTime value={message.updated_at} mode="absolute" />
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          tone="neutral"
          size="sm"
          startIcon={<Icon icon={Pencil} size="sm" />}
          onClick={() => onEdit(message)}
        >
          {isReadOnly ? t("admin.editPlacement") : t("admin.edit")}
        </Button>

        <Button
          variant={isPublished ? "ghost" : "solid"}
          tone={isPublished ? "neutral" : "accent"}
          size="sm"
          loading={update.isPending}
          onClick={() =>
            update.mutate({
              messageId: message.message_id,
              patch: { published: !isPublished },
            })
          }
        >
          {isPublished ? t("admin.unpublish") : t("admin.publish")}
        </Button>

        <Button
          variant="ghost"
          tone="neutral"
          size="sm"
          startIcon={<Icon icon={Clock} size="sm" />}
          onClick={() => onShowHistory(message.message_id)}
        >
          {t("admin.history")}
        </Button>

        {isReadOnly ? null : (
          <Button
            variant="ghost"
            tone="danger"
            size="sm"
            startIcon={<Icon icon={Trash2} size="sm" />}
            onClick={() => setConfirmingDelete(true)}
          >
            {t("admin.delete")}
          </Button>
        )}
      </div>

      <AlertDialog.Root open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialog.Portal>
          <AlertDialog.Backdrop />
          <AlertDialog.Popup className="flex flex-col gap-4">
            <AlertDialog.Title>{t("admin.deleteTitle")}</AlertDialog.Title>
            <AlertDialog.Description>
              {t("admin.deleteBody", { title: message.title })}
            </AlertDialog.Description>
            <div className="flex flex-wrap justify-end gap-2">
              <AlertDialog.Close render={<Button variant="outline" tone="neutral" />}>
                {t("form.cancel")}
              </AlertDialog.Close>
              <Button
                tone="danger"
                loading={remove.isPending}
                onClick={() =>
                  remove.mutate(message.message_id, {
                    onSuccess: () => setConfirmingDelete(false),
                  })
                }
              >
                {t("admin.deleteConfirm")}
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </Card>
  );
}
